import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { ComponentWithTooltip } from 'components/ComponentWithTooltip';
import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { ControlledDateField, ControlledTextField } from 'components/Form';
import { ModalDialog } from 'components/ModalDialog';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import type { ActKc3Input } from 'gql/graphql';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';
import { useProjectData } from 'hooks/useProjectData';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';
import { IconAlert, IconCalendar, IconQuestion, Informer, Loader, Text } from 'uikit';
import { requiredBaseRule } from 'utils/constants';
import { disableKeyButNumber } from 'utils/disableKeyButNumber';
import { getDateIncludingCurrentTime } from 'utils/formatDateToLocale';
import { replacerCost } from 'utils/replacerCost';

import { debounce } from 'debounce';
import * as R from 'ramda';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { createActKC3Gql, isUniqueActKC3InternalNumberGql, updateActKC3Gql } from './addOrEditActKc3Queries';
import { useActKC3Data } from './hooks/useActKC3Data';
import { useAddOrEditActsKC3Columns } from './hooks/useAddOrEditActsKC3Columns';
import { useIsValidActKC3Date } from './hooks/useIsValidActKC3Date';

import Style from './AddOrEditActKc3Modal.module.css';

interface IAddOrEditActKc3Modal {
    contractId: number;
    onClose?: (hasSaved?: boolean) => void;
    actKC3Id?: number;
    isReadonly?: boolean;
    isCorrection?: boolean;
    workOrderId?: number;
    rootContractObjectId?: number;
    isOnlyDateEditable?: boolean;
    isFinal?: boolean;
}

export const AddOrEditActKc3Modal: FC<IAddOrEditActKc3Modal> = (props) => {
    const { id: projectId } = useParams();

    const { projectData } = useProjectData(projectId, false);

    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

    const {
        contractId,
        onClose,
        actKC3Id,
        isReadonly,
        isCorrection,
        workOrderId,
        rootContractObjectId,
        isOnlyDateEditable,
        isFinal,
    } = props;
    const {
        control,
        watch,
        handleSubmit,
        reset,
        setError,
        clearErrors,
        formState: { dirtyFields, isDirty },
    } = useForm();
    const dispatch = useDispatch();

    const [isUniqueActKC3InternalNumber, setIsUniqueActKC3InternalNumber] = useState(true);

    const [createActKC3Data, createActKC3Status, , createActKC3Request, createActKC3SetData] = useRequest({
        requestId: 'createActKC3',
        query: createActKC3Gql,
        withEffect: false,
    });

    const [updateActKC3Data, updateActKC3Status, , updateActKC3Request, updateActKC3SetData] = useRequest({
        requestId: 'updateActKC3',
        query: updateActKC3Gql,
        withEffect: false,
    });

    const { columns, checkedRowsIds, setCheckedRows, filterValues } = useAddOrEditActsKC3Columns({
        isReadonly,
        isCorrection,
        contractId,
        actKC3Id,
        workOrderId,
        rootContractObjectId,
        isFinal,
    });

    const {
        formData,
        rowData: items,
        resetActKc3Data,
    } = useActKC3Data({ actKC3Id, isReadonly, contractId, filterValues, workOrderId, rootContractObjectId });

    const [
        isUniqueActKC3InternalNumberData,
        isUniqueActKC3InternalNumberStatus,
        ,
        isUniqueActKC3InternalNumberRequest,
        isUniqueActKC3InternalNumberSetData,
    ] = useRequest({
        requestId: 'isUniqueActKC3InternalNumber',
        query: isUniqueActKC3InternalNumberGql,
        withEffect: false,
    });

    useEffect(() => {
        if (
            isUniqueActKC3InternalNumberStatus === RequestStatus.SUCCESS &&
            !isUniqueActKC3InternalNumberData?.uerIsUniqueActKC3InternalNumber?.result
        ) {
            setIsUniqueActKC3InternalNumber(false);
        } else {
            setIsUniqueActKC3InternalNumber(true);
        }
    }, [isUniqueActKC3InternalNumberData?.uerIsUniqueActKC3InternalNumber?.result, isUniqueActKC3InternalNumberStatus]);

    useEffect(() => {
        if (!isUniqueActKC3InternalNumber) {
            setError('number', { message: 'Справка КС-3 с таким номером уже существует. Введите другой номер' });
        } else {
            clearErrors('number');
        }
    }, [clearErrors, isUniqueActKC3InternalNumber, setError]);

    useEffect(() => {
        if (actKC3Id && formData) {
            const values = {
                number: isCorrection ? formData.fullNumber : formData.number,
                date: formData.date,
                reportingStartDate: formData.reportingStartDate,
                reportingFinishDate: formData.reportingFinishDate,
            };

            reset(values);

            setCheckedRows(formData.actKC2Ids);
        }
    }, [actKC3Id, formData, isCorrection, reset, setCheckedRows]);

    const analyticsData = useMemo(
        () => ({
            projectId,
            contractId,
            orderId: workOrderId,
            methodology: projectData?.methodology,
            projectName: projectData?.name,
            projectType: projectData?.contractType,
            projectDO: projectData?.subsidiary?.name,
        }),
        [
            contractId,
            projectData?.contractType,
            projectData?.methodology,
            projectData?.name,
            projectData?.subsidiary?.name,
            projectId,
            workOrderId,
        ],
    );

    useEffect(() => {
        if (createActKC3Data?.uerCreateActKC3?.result && createActKC3Status === RequestStatus.SUCCESS) {
            sendGpnAnalyticsEvent({
                componentId: 'rim_KS TRI file_created',
                ...analyticsData,
                KC3Id: createActKC3Data?.uerCreateActKC3?.data?.id,
            });
        }
    }, [
        analyticsData,
        createActKC3Data?.uerCreateActKC3?.data?.id,
        createActKC3Data?.uerCreateActKC3?.result,
        createActKC3Status,
        sendGpnAnalyticsEvent,
    ]);

    useEffect(() => {
        if (
            (createActKC3Data?.uerCreateActKC3?.result && createActKC3Status === RequestStatus.SUCCESS) ||
            (updateActKC3Data?.uerUpdateActKC3?.result && updateActKC3Status === RequestStatus.SUCCESS)
        ) {
            resetActKc3Data();
            reset();
            onClose?.(true);

            dispatch(createActKC3SetData(undefined));
            dispatch(updateActKC3SetData(undefined));
            dispatch(isUniqueActKC3InternalNumberSetData(undefined));
        }
    }, [
        createActKC3Data?.uerCreateActKC3?.result,
        createActKC3SetData,
        createActKC3Status,
        dispatch,
        isUniqueActKC3InternalNumberSetData,
        onClose,
        reset,
        resetActKc3Data,
        updateActKC3Data?.uerUpdateActKC3?.result,
        updateActKC3SetData,
        updateActKC3Status,
    ]);

    const isConfirmDisabled = useMemo(
        () =>
            updateActKC3Status === RequestStatus.FETCHING ||
            createActKC3Status === RequestStatus.FETCHING ||
            !checkedRowsIds.length ||
            !isUniqueActKC3InternalNumber,
        [checkedRowsIds.length, createActKC3Status, isUniqueActKC3InternalNumber, updateActKC3Status],
    );

    const actKC3Number = useWatch({
        control,
        name: 'number',
    });

    const onNumberSearch = useCallback(
        (number) => {
            dispatch(
                isUniqueActKC3InternalNumberRequest({
                    actKC3Id,
                    actKC3Number: R.isNil(number) ? null : replacerCost(number),
                    contractId,
                }),
            );
        },
        [actKC3Id, contractId, dispatch, isUniqueActKC3InternalNumberRequest],
    );

    const debouncedSearch = useMemo(() => debounce(onNumberSearch, 500), [onNumberSearch]);

    useEffect(() => {
        if (!isReadonly && !isCorrection && !R.isNil(actKC3Number) && dirtyFields.number) {
            debouncedSearch(actKC3Number);
        } else {
            setIsUniqueActKC3InternalNumber(true);
        }
    }, [actKC3Number, debouncedSearch, dirtyFields.number, isCorrection, isReadonly]);

    const doEditCancel = useCallback(() => {
        dispatch(isUniqueActKC3InternalNumberSetData(undefined));
        resetActKc3Data();
        reset();
        onClose?.(false);
    }, [dispatch, isUniqueActKC3InternalNumberSetData, onClose, reset, resetActKc3Data]);

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn: doEditCancel,
        });

    const onEditCancel = useCallback(() => {
        if (!isDirty) {
            doEditCancel();
        } else {
            handleOpenConfirmationDialog();
        }
    }, [doEditCancel, handleOpenConfirmationDialog, isDirty]);

    const onEditConfirm = useCallback(
        (submitData: Record<string, any>) => {
            const baseActKC3Input: ActKc3Input = {
                contractId,
                actKC2Ids: checkedRowsIds,
                date: submitData.date ? getDateIncludingCurrentTime(submitData.date) : undefined,
                // eslint-disable-next-line no-nested-ternary
                number: isCorrection
                    ? formData?.number
                    : R.isNil(submitData.number)
                    ? null
                    : replacerCost(submitData.number),
                workOrderId,
                rootContractObjectId,
            };

            if (actKC3Id) {
                dispatch(updateActKC3Request({ actKC3Input: { ...baseActKC3Input, id: actKC3Id } }));
            } else {
                dispatch(createActKC3Request({ actKC3Input: baseActKC3Input }));

                sendGpnAnalyticsEvent({
                    componentId: 'rim_create KS TRI button_pressed',
                    ...analyticsData,
                });
            }
        },
        [
            actKC3Id,
            analyticsData,
            checkedRowsIds,
            contractId,
            createActKC3Request,
            dispatch,
            formData?.number,
            isCorrection,
            rootContractObjectId,
            sendGpnAnalyticsEvent,
            updateActKC3Request,
            workOrderId,
        ],
    );

    const title = useMemo(() => {
        const correctionTitle = isCorrection ? ' корректировочной ' : ' ';
        const viewTitle = `Просмотр${correctionTitle}справки КС-3`;
        const modalTitle = `Формирование${correctionTitle}справки КС-3`;
        const editModalTitle = `Редактирование${correctionTitle}справки КС-3`;

        if (isReadonly) {
            return viewTitle;
        }

        return actKC3Id ? editModalTitle : modalTitle;
    }, [actKC3Id, isCorrection, isReadonly]);

    const btnConfirmLabel = useMemo(() => {
        if (isReadonly) {
            return 'Закрыть';
        }

        return actKC3Id ? 'Сохранить' : 'Сформировать';
    }, [actKC3Id, isReadonly]);

    const parseValueNumber = useCallback((event) => disableKeyButNumber(event), []);

    const dateValue = watch('date');

    const { dataUerIsValidActKC3Date, statusIsValidActKC3Date } = useIsValidActKC3Date({
        date: dateValue,
        actKC3Id,
    });

    useEffect(() => {
        if (dateValue && statusIsValidActKC3Date === RequestStatus.SUCCESS && !dataUerIsValidActKC3Date?.result) {
            setError('date', {
                message:
                    'Дата должна находиться в границах: больше или равно дате составления предыдущей справки КС-3, но не позже месяца от текущей даты',
            });
        } else {
            clearErrors('date');
        }
    }, [clearErrors, dataUerIsValidActKC3Date?.result, dateValue, setError, statusIsValidActKC3Date]);

    return (
        <>
            <ModalDialog
                title={title}
                btnConfirmLabel={btnConfirmLabel}
                btnCancelLabel={isReadonly ? btnConfirmLabel : undefined}
                btnCancelView={isReadonly ? 'primary' : undefined}
                isOpen
                hasOverlay
                withTitleCloseButton
                onClose={onEditCancel}
                onConfirm={isReadonly ? undefined : handleSubmit(onEditConfirm)}
                btnConfirmDisabled={isConfirmDisabled}
                isOnClickOutsideDisable
            >
                {actKC3Id && !formData ? (
                    <div className={Style.addOrEditActKc3Modal}>
                        <Loader />
                    </div>
                ) : (
                    <div className={Style.addOrEditActKc3Modal}>
                        <div className={Style.actKc3Info}>
                            <div className={Style.rowWrapper}>
                                <ControlledTextField
                                    control={control}
                                    name="number"
                                    label="Номер справки КС-3"
                                    size="s"
                                    required
                                    incrementButtons={false}
                                    placeholder="—"
                                    directionTooltip="upCenter"
                                    disabled={isCorrection || isReadonly}
                                    className={Style.numberCell}
                                    rules={requiredBaseRule}
                                    onKeyDown={parseValueNumber}
                                />
                                <ControlledDateField
                                    control={control}
                                    label="от"
                                    name="date"
                                    type="date"
                                    rightSide={IconCalendar}
                                    disabled={isReadonly && !isOnlyDateEditable}
                                    required
                                    rules={requiredBaseRule}
                                    directionTooltip="upCenter"
                                    className={Style.dateCell}
                                    tooltipSize="m"
                                />
                            </div>
                            <div className={Style.rowWrapper}>
                                <Text view="secondary" size="s" className={Style.reportingDateLabel}>
                                    Отчетный период
                                    <ComponentWithTooltip tooltipText="Отчетный период формируется на основе отчетных периодов добавленных Актов КС-2">
                                        <IconQuestion size="s" />
                                    </ComponentWithTooltip>
                                </Text>
                                <div className={Style.reportingDateBlock}>
                                    <ControlledDateField
                                        control={control}
                                        name="reportingStartDate"
                                        type="date"
                                        disabled
                                        placeholder="ДД.ММ.ГГГГ"
                                        view="clear"
                                        withClearButton={false}
                                        className={Style.reportingDate}
                                    />
                                    <Text view="ghost" style={{ padding: '0 2px' }}>
                                        -
                                    </Text>
                                    <ControlledDateField
                                        control={control}
                                        name="reportingFinishDate"
                                        type="date"
                                        disabled
                                        placeholder="ДД.ММ.ГГГГ"
                                        view="clear"
                                        withClearButton={false}
                                        className={Style.reportingDate}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className={Style.actsKc2Table}>
                            <AgGridTable
                                rowData={items}
                                columnDefs={columns}
                                tooltipShowDelay={200}
                                maxConcurrentDatasourceRequests={1}
                                maxBlocksInCache={40}
                                cacheBlockSize={40}
                            />
                        </div>

                        <Informer status="system" size="s" icon={IconAlert}>
                            {!actKC3Id
                                ? 'Для формирования справки КС-3 необходимо выбрать хотя бы один Акт КС-2'
                                : 'Справка КС-3 должна содержать хотя бы один Акт КС-2'}
                        </Informer>
                    </div>
                )}
            </ModalDialog>
            <ConfirmationDialog
                isOpened={isOpenConfirmationDialog}
                onSubmit={handleActionQuery}
                onClose={handleCloseConfirmationDialog}
                textHeader="Отмена действия"
                textConfirmation="Вы уверены, что хотите отменить? Данные не будут сохранены"
                cancelButtonText="Не отменять"
                confirmButtonText="Отменить "
            />
        </>
    );
};

AddOrEditActKc3Modal.displayName = 'AddOrEditActKc3Modal';
